import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { ProtectedRoute } from '@/router/ProtectedRoute'
import { RequireSuperadmin } from '@/router/RequireSuperadmin'

import Home from '@/pages/Home'

// Auth pages
import Login          from '@/pages/auth/Login'
import Register       from '@/pages/auth/Register'
import AdminLogin     from '@/pages/auth/AdminLogin'
import ForgotPassword from '@/pages/auth/ForgotPassword'
import ResetPassword  from '@/pages/auth/ResetPassword'
import SelectRole     from '@/pages/auth/SelectRole'
import AuthCallback   from '@/pages/auth/AuthCallback'
import CheckEmail     from '@/pages/auth/CheckEmail'
import AcceptInstructorInvite from '@/pages/auth/AcceptInstructorInvite'
import AcceptAdminInvite      from '@/pages/auth/AcceptAdminInvite'

// App shell + portals
import AppShell              from '@/pages/app/AppShell'
import SchoolOverview        from '@/pages/app/school/SchoolOverview'
import SchoolOnboardingWizard from '@/pages/app/school/OnboardingWizard'
import Instructors           from '@/pages/app/school/Instructors'
import Contractors           from '@/pages/app/school/Contractors'
import Fleet                 from '@/pages/app/school/Fleet'
import EmployeeDetail        from '@/pages/app/school/EmployeeDetail'
import SchoolStudents        from '@/pages/app/school/Students'
import TrackingSheets        from '@/pages/app/school/TrackingSheets'
import SchoolInstructorCalendar from '@/pages/app/school/InstructorCalendar'
import InstructorDashboard   from '@/pages/app/instructor/InstructorDashboard'
import InstructorOnboarding from '@/pages/app/instructor/InstructorOnboarding'
import Students             from '@/pages/app/instructor/Students'
import StudentDetail        from '@/pages/app/instructor/StudentDetail'
import Schools              from '@/pages/app/instructor/Schools'
import InstructorCalendar   from '@/pages/app/instructor/Calendar'
import StudentHome           from '@/pages/app/student/StudentHome'
import StudentCourse         from '@/pages/app/student/StudentCourse'
import StudentCalendar       from '@/pages/app/student/Calendar'
import MyInstructor          from '@/pages/app/student/MyInstructor'
import Settings              from '@/pages/app/shared/Settings'
import PlaceholderPage       from '@/pages/app/shared/PlaceholderPage'
import Library                from '@/pages/app/instructor/Library'
import AdminSchools          from '@/pages/app/admin/AdminSchools'
import AdminInstructors      from '@/pages/app/admin/AdminInstructors'
import AdminStudents         from '@/pages/app/admin/AdminStudents'
import AdminAdmins           from '@/pages/app/admin/AdminAdmins'
import AdminCurriculum       from '@/pages/app/admin/AdminCurriculum'

export default function App() {
  const { loading } = useAuth()

  if (loading) return <LoadingScreen />

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path='/login'          element={<Login />} />
      <Route path='/register'       element={<Register />} />
      {/* Admin has its own login page: no Google OAuth, no self-register link, since
          drivetrack_admin cannot self-register (migration 028) — the only way in is
          accepting a superadmin's invite. */}
      <Route path='/admin/login'    element={<AdminLogin />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/auth/reset-password' element={<ResetPassword />} />
      <Route path='/auth/select-role'    element={<SelectRole />} />
      <Route path='/auth/callback'       element={<AuthCallback />} />
      <Route path='/auth/check-email'    element={<CheckEmail />} />
      {/* Public: reachable by a signed-out invitee following an email link. Handles its
          own auth gating internally rather than via ProtectedRoute, since it must work
          both signed-in and signed-out. */}
      <Route path='/accept-instructor-invite' element={<AcceptInstructorInvite />} />
      <Route path='/accept-admin-invite'      element={<AcceptAdminInvite />} />

      {/* School onboarding wizard — outside AppShell, no nav chrome, same pattern as
          /app/instructor/onboarding */}
      <Route path='/app/school/onboarding-wizard' element={
        <ProtectedRoute requiredRole='school_admin'>
          <SchoolOnboardingWizard />
        </ProtectedRoute>
      } />

      {/* School admin portal */}
      <Route path='/app/school' element={
        <ProtectedRoute requiredRole='school_admin'>
          <AppShell role='school_admin' />
        </ProtectedRoute>
      }>
        <Route index element={<SchoolOverview />} />
        <Route path='instructors'      element={<Instructors />} />
        <Route path='instructors/:membershipId' element={<EmployeeDetail />} />
        <Route path='contractors'      element={<Contractors />} />
        <Route path='fleet'            element={<Fleet />} />
        <Route path='students'         element={<SchoolStudents />} />
        <Route path='tracking-sheets'  element={<TrackingSheets />} />
        <Route path='calendar'         element={<SchoolInstructorCalendar />} />
        <Route path='reports'          element={<PlaceholderPage title='Reports' />} />
        <Route path='settings'         element={<Settings />} />
      </Route>

      {/* Instructor onboarding — outside AppShell, no nav chrome */}
      <Route path='/app/instructor/onboarding' element={
        <ProtectedRoute requiredRole='instructor'>
          <InstructorOnboarding />
        </ProtectedRoute>
      } />

      {/* Instructor portal */}
      <Route path='/app/instructor' element={
        <ProtectedRoute requiredRole='instructor'>
          <AppShell role='instructor' />
        </ProtectedRoute>
      }>
        <Route index element={<InstructorDashboard />} />
        {/* Enroll is now the Students page's "Invites" tab — this redirect keeps any
            existing links/bookmarks to the old standalone page working. */}
        <Route path='enroll' element={<Navigate to='/app/instructor/students' state={{ tab: 'invites', openForm: true }} replace />} />
        <Route path='students'          element={<Students />} />
        {/* Keyed by relationshipId, not studentId: a student can have more than one
            relationship row with the same instructor over time (ended + re-enrolled,
            migration 059), so studentId alone would be ambiguous about which one to load. */}
        <Route path='students/:relationshipId' element={<StudentDetail />} />
        <Route path='schools'           element={<Schools />} />
        <Route path='employment-record' element={<EmployeeDetail />} />
        <Route path='library'   element={<Library />} />
        <Route path='calendar'  element={<InstructorCalendar />} />
        <Route path='mock'      element={<PlaceholderPage title='Mock Test' />} />
        <Route path='settings'  element={<Settings />} />
      </Route>

      {/* Student portal */}
      <Route path='/app/student' element={
        <ProtectedRoute requiredRole='student'>
          <AppShell role='student' />
        </ProtectedRoute>
      }>
        <Route index element={<StudentHome />} />
        <Route path='course'      element={<StudentCourse />} />
        <Route path='instructor'  element={<MyInstructor />} />
        <Route path='calendar'    element={<StudentCalendar />} />
        <Route path='checklist'   element={<PlaceholderPage title='Checklist' />} />
        <Route path='mock'        element={<PlaceholderPage title='Mock Test' />} />
        <Route path='certificate' element={<PlaceholderPage title='Certificate' />} />
        <Route path='settings'    element={<Settings />} />
      </Route>

      {/* Platform admin portal */}
      <Route path='/app/admin' element={
        <ProtectedRoute requiredRole='drivetrack_admin'>
          <AppShell role='drivetrack_admin' />
        </ProtectedRoute>
      }>
        <Route index element={<AdminSchools />} />
        <Route path='students'    element={<AdminStudents />} />
        <Route path='instructors' element={<AdminInstructors />} />
        <Route path='e-learning'  element={<AdminCurriculum />} />
        <Route path='admins'      element={<RequireSuperadmin><AdminAdmins /></RequireSuperadmin>} />
      </Route>

      {/* Home / catch-all */}
      <Route path='/' element={<Home />} />
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  )
}
